"use client";

/**
 * BackgroundMediaPicker — choose a background from the Media Lab.
 *
 * Browses the visual assets the user already has on BB: video files (mp4/webm/
 * mov) and stills (jpg/png/gif/webp) from their works, plus release cover art
 * and gallery images. Picking one hands back a URL, so the caller keeps full
 * control of how it is rendered.
 *
 * Built for the sing-along stage but deliberately generic — the lyrics-video
 * and Film Maker backgrounds want exactly the same picker.
 *
 * Admins see every asset on the platform; everyone else sees only their own,
 * matching how the Media Vault itself scopes access.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { Loader2, ImageIcon, Film, X, Search, Sparkles } from "lucide-react";
import { db } from "@/lib/firebase";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const VIDEO_RE = /\.(mp4|webm|mov|m4v)(\?|$)/i;
const GIF_RE = /\.gif(\?|$)/i;

export interface BackgroundAsset {
  id: string;
  url: string;
  title: string;
  kind: "video" | "gif" | "image";
  /** Still to show in the grid — a video has no inline thumbnail of its own. */
  poster?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (asset: BackgroundAsset) => void;
  /** Currently selected URL, highlighted in the grid. */
  value?: string;
}

function classify(url: string): BackgroundAsset["kind"] {
  if (VIDEO_RE.test(url)) return "video";
  if (GIF_RE.test(url)) return "gif";
  return "image";
}

export function BackgroundMediaPicker({ open, onOpenChange, onPick, value }: Props) {
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const [assets, setAssets] = useState<BackgroundAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<"all" | "video" | "gif" | "image">("all");

  const isAdmin =
    userProfile?.roles?.includes("admin") ||
    userProfile?.roles?.includes("superadmin") ||
    userProfile?.accountType === "admin" ||
    userProfile?.accountType === "superadmin";

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Non-admins only ever query their OWN works — scoping here rather than
      // fetching everything and filtering client-side keeps other people's
      // unreleased material off the wire entirely.
      const worksQ = isAdmin
        ? collection(db, "works")
        : query(collection(db, "works"), where("owner.userId", "==", user.uid));
      const [worksSnap, relSnap] = await Promise.all([
        getDocs(worksQ as any),
        getDocs(collection(db, "releases")),
      ]);

      const found: BackgroundAsset[] = [];
      const seen = new Set<string>();
      const push = (url: unknown, title: string, id: string, poster?: string) => {
        if (typeof url !== "string" || !url.startsWith("http")) return;
        if (seen.has(url)) return;
        seen.add(url);
        found.push({ id, url, title: title || "Untitled", kind: classify(url), poster });
      };

      worksSnap.docs.forEach(d => {
        const w = d.data() as any;
        const title = w.title || w.name || "Untitled";
        // A work's video is the most useful background; its artwork is next.
        push(w.videoUrl, title, `${d.id}-v`, w.coverArtUrl || w.thumbnailUrl || w.imageUrl);
        push(w.imageUrl, title, `${d.id}-i`);
        push(w.coverArtUrl, title, `${d.id}-c`);
        if (Array.isArray(w.images)) w.images.forEach((u: string, i: number) => push(u, title, `${d.id}-g${i}`));
      });

      relSnap.docs.forEach(d => {
        const r = d.data() as any;
        // Releases aren't owner-scoped in the same shape, so filter by artist
        // ownership where we can, and let admins see the lot.
        if (!isAdmin && r.ownerUserId && r.ownerUserId !== user.uid) return;
        const title = r.title || "Release";
        push(r.coverArtUrl, title, `${d.id}-rc`);
        push(r.imageUrl, title, `${d.id}-ri`);
        if (Array.isArray(r.images)) r.images.forEach((u: string, i: number) => push(u, title, `${d.id}-rg${i}`));
      });

      setAssets(found);
      setLoaded(true);
    } catch (err) {
      console.error("[BackgroundMediaPicker]", err);
      toast({ description: "Couldn't load your media.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin, toast]);

  useEffect(() => { if (open && !loaded && !loading) load(); }, [open, loaded, loading, load]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return assets.filter(a =>
      (kind === "all" || a.kind === kind) &&
      (!needle || a.title.toLowerCase().includes(needle)),
    );
  }, [assets, q, kind]);

  const counts = useMemo(() => ({
    all: assets.length,
    video: assets.filter(a => a.kind === "video").length,
    gif: assets.filter(a => a.kind === "gif").length,
    image: assets.filter(a => a.kind === "image").length,
  }), [assets]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-4 pb-3 space-y-3">
          <DialogTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> Background from your Media Lab
          </DialogTitle>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input type="search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search your media…"
              className="pl-8 h-9"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {([
              ["all", "All"], ["video", "Video"], ["gif", "GIF"], ["image", "Images"],
            ] as const).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={cn(
                  "px-2 py-0.5 rounded text-[11px] border transition-colors",
                  kind === k
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:bg-muted",
                )}
              >
                {label} {counts[k] > 0 && <span className="opacity-60">{counts[k]}</span>}
              </button>
            ))}
          </div>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto border-t border-border p-3">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your media…
            </div>
          )}

          {!loading && !results.length && (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {assets.length
                ? "Nothing matches that filter."
                : "No images or video found in your Media Lab yet. Upload artwork or a video and it'll show up here."}
            </p>
          )}

          {!loading && !!results.length && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {results.map(a => {
                const isActive = value === a.url;
                const still = a.kind === "video" ? a.poster : a.url;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => { onPick(a); onOpenChange(false); }}
                    className={cn(
                      "group relative aspect-video rounded-lg overflow-hidden border bg-muted text-left",
                      isActive ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary/60",
                    )}
                    title={a.title}
                  >
                    {still ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={still} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Film className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    <span className="absolute top-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white flex items-center gap-1">
                      {a.kind === "video" ? <><Film className="h-2.5 w-2.5" /> Video</>
                        : a.kind === "gif" ? "GIF"
                        : <><ImageIcon className="h-2.5 w-2.5" /> Image</>}
                    </span>
                    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-1.5 pb-1 pt-4 text-[10px] text-white truncate">
                      {a.title}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!!value && (
          <div className="p-3 border-t border-border bg-muted/30 flex items-center justify-between gap-2">
            <p className="text-[11px] text-muted-foreground truncate">Current background set</p>
            <button
              type="button"
              onClick={() => { onPick({ id: "", url: "", title: "", kind: "image" }); onOpenChange(false); }}
              className="text-[11px] text-destructive hover:underline inline-flex items-center gap-1 shrink-0"
            >
              <X className="h-3 w-3" /> Clear background
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
